import { createContext, useCallback, useContext, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * App-wide confirm dialog replacing browser-native `window.confirm()`, which
 * is blocked in Emergent's sandboxed preview iframe (no `allow-modals`). Wrap
 * the app once with <ConfirmProvider/>, then call `useConfirm()` anywhere:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Delete user?", body: "...", destructive: true })) {
 *     await api.delete(...);
 *   }
 *
 * Returns a promise<bool> — true if confirmed, false on cancel / dismiss.
 */
const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);  // { title, body, confirmLabel, destructive, resolve }
  const [busy, setBusy] = useState(false);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts.title || "Are you sure?",
        body: opts.body || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        destructive: !!opts.destructive,
        resolve,
      });
    });
  }, []);

  const close = (value) => {
    if (!state) return;
    state.resolve(value);
    setState(null);
    setBusy(false);
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Dialog open={!!state} onOpenChange={(v) => { if (!v && !busy) close(false); }}>
        <DialogContent className="max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>{state?.title}</DialogTitle>
          </DialogHeader>
          {state?.body && (
            <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{state.body}</p>
          )}
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => close(false)}
              data-testid="confirm-cancel"
            >
              Cancel
            </Button>
            <Button
              disabled={busy}
              onClick={() => { setBusy(true); close(true); }}
              className={state?.destructive ? "bg-red-600 hover:bg-red-700 text-white" : ""}
              data-testid="confirm-ok"
            >
              {state?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    throw new Error("useConfirm() must be used inside <ConfirmProvider/>");
  }
  return ctx;
}
